import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { requestPin } from "@/components/PinGate";
import { DatabaseBackup, Package, RotateCcw, Trash2 } from "lucide-react";

const fmtWIB = (iso) => new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " WIB";
const TYPE_LABEL = { products: "📦 Produk & Harga", full: "🗄 Seluruh Sistem" };

export default function BackupTab() {
  const [backups, setBackups] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [restoring, setRestoring] = useState("");

  const load = () => api.get("/admin/backups").then((r) => setBackups(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat daftar backup"));
  useEffect(() => { load(); }, []);

  const createBackup = async (type) => {
    if (busy) return;
    setBusy(type);
    try {
      const r = await api.post("/admin/backups", { type, note });
      toast.success(`Backup ${TYPE_LABEL[type]} berhasil — ${r.data.total_docs} dokumen tersimpan`);
      setNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat backup"); }
    finally { setBusy(""); }
  };

  const restore = async (b) => {
    const label = b.type === "full" ? "SELURUH SISTEM" : "data PRODUK & HARGA";
    if (!window.confirm(`Restore ${label} dari backup ${fmtWIB(b.created_at)}?\n\nSEMUA data saat ini akan digantikan oleh isi backup ini. Lanjutkan?`)) return;
    const pin = await requestPin();
    if (!pin) return;
    setRestoring(b.id);
    try {
      const r = await api.post(`/admin/backups/${b.id}/restore`, { pin });
      const total = Object.values(r.data.restored || {}).reduce((a, c) => a + c, 0);
      toast.success(`Restore selesai — ${total} dokumen dikembalikan. Muat ulang halaman untuk melihat data terbaru.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore gagal"); }
    finally { setRestoring(""); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Hapus backup ${fmtWIB(b.created_at)} secara permanen?`)) return;
    const pin = await requestPin();
    if (!pin) return;
    try {
      await api.delete(`/admin/backups/${b.id}?pin=${pin}`);
      toast.success("Backup dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus backup"); }
  };

  const countsText = (b) => {
    const c = b.counts || {};
    if (b.type === "products") return `${c.products || 0} produk (nama, kategori, harga modal, harga jual, grosir, stok per cabang, barcode, gambar)`;
    return Object.entries(c).map(([k, v]) => `${k}: ${v}`).join(" · ");
  };

  return (
    <div className="mt-6 space-y-4" data-testid="backup-tab">
      <div className="bg-white rounded-3xl border border-[#E5DECF] p-5">
        <h3 className="font-display font-bold text-xl">💾 Backup Data</h3>
        <p className="text-sm text-stone-500 mt-1">Amankan data agar bisa dikembalikan jika website gangguan atau data tidak sengaja terhapus/berubah. Tekan <b>Restore Data</b> pada salah satu backup untuk mengembalikannya.</p>
        <Input data-testid="backup-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan backup (opsional), mis: sebelum input belanja besar" className="mt-3" />
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button data-testid="backup-products-btn" onClick={() => createBackup("products")} disabled={!!busy} className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 p-4 text-left transition disabled:opacity-50">
            <div className="flex items-center gap-2 font-bold text-emerald-800"><Package className="w-4 h-4" /> Backup Produk & Harga</div>
            <div className="text-xs text-stone-500 mt-1">Katalog produk: nama, harga modal, harga jual, grosir, stok per cabang, barcode, foto.</div>
            {busy === "products" && <div className="text-xs font-bold text-amber-600 mt-1">⏳ Membuat backup…</div>}
          </button>
          <button data-testid="backup-full-btn" onClick={() => createBackup("full")} disabled={!!busy} className="rounded-2xl border-2 border-stone-300 bg-stone-50 hover:bg-stone-100 p-4 text-left transition disabled:opacity-50">
            <div className="flex items-center gap-2 font-bold text-stone-800"><DatabaseBackup className="w-4 h-4" /> Backup Seluruh Sistem</div>
            <div className="text-xs text-stone-500 mt-1">Produk + pesanan, penjualan kasir, keuangan, pelanggan, cabang, pengguna, riwayat harga, pengaturan.</div>
            {busy === "full" && <div className="text-xs font-bold text-amber-600 mt-1">⏳ Membuat backup…</div>}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#E5DECF] p-5">
        <h4 className="font-display font-bold text-lg">Daftar Backup {backups ? `(${backups.length})` : ""}</h4>
        {!backups && <p className="text-sm text-stone-400 mt-2">Memuat…</p>}
        {backups && backups.length === 0 && <p className="text-sm text-stone-400 mt-2" data-testid="backup-empty">Belum ada backup. Buat backup pertama Anda di atas.</p>}
        <div className="mt-3 space-y-2">
          {(backups || []).map((b) => (
            <div key={b.id} data-testid={`backup-item-${b.id}`} className="rounded-2xl border border-[#E5DECF] p-4 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`tag-pill ${b.type === "full" ? "bg-stone-900 text-white" : "bg-emerald-100 text-emerald-800"}`}>{TYPE_LABEL[b.type] || b.type}</span>
                  <span className="font-bold text-sm">{fmtWIB(b.created_at)}</span>
                  <span className="text-xs text-stone-400">oleh {b.created_by}</span>
                </div>
                {b.note && <div className="text-xs text-stone-600 mt-1">📝 {b.note}</div>}
                <div className="text-[11px] text-stone-400 mt-1 break-words">{countsText(b)}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`backup-restore-${b.id}`} onClick={() => restore(b)} disabled={restoring === b.id} className="rounded-full bg-emerald-700 hover:bg-emerald-800 font-bold">
                  <RotateCcw className="w-4 h-4 mr-1" /> {restoring === b.id ? "Memulihkan…" : "Restore Data"}
                </Button>
                <button data-testid={`backup-delete-${b.id}`} onClick={() => remove(b)} title="Hapus backup (PIN)" className="w-9 h-9 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center hover:bg-rose-100"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
